<script setup>
import { computed, ref, watch } from 'vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const hovered = ref(false);

const isPublicChatMessage = message => {
  const messageType = message?.message_type ?? message?.messageType;
  return (
    message &&
    message.private !== true &&
    messageType !== 2 &&
    messageType !== 'activity'
  );
};

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => {
  const messages = props.chat.messages || [];
  const lastPublicMessageInStore = [...messages]
    .reverse()
    .find(isPublicChatMessage);
  const lastPublicMessageFromAPI = props.chat.last_non_activity_message;

  if (lastPublicMessageInStore && lastPublicMessageFromAPI) {
    return lastPublicMessageInStore.created_at >=
      lastPublicMessageFromAPI.created_at
      ? lastPublicMessageInStore
      : lastPublicMessageFromAPI;
  }

  return lastPublicMessageInStore || lastPublicMessageFromAPI || null;
});

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const sourceVisual = computed(() => {
  const name = String(props.inbox?.name || '').toLowerCase();
  const channelType = props.inbox?.channel_type || props.inbox?.channelType;

  if (name.includes('max')) {
    return { icon: 'i-lucide-message-circle', className: 'bg-[#7657FF]' };
  }

  if (
    channelType === 'Channel::Telegram' ||
    name.includes('telegram') ||
    name.endsWith('bot')
  ) {
    return { icon: 'i-ri-telegram-fill', className: 'bg-[#229ED9]' };
  }

  if (channelType === 'Channel::Whatsapp' || name.includes('whatsapp')) {
    return { icon: 'i-ri-whatsapp-fill', className: 'bg-[#25D366]' };
  }

  return {
    icon: getInboxIconByType(channelType, props.inbox?.medium),
    className: 'bg-n-slate-10',
  };
});

const lastMessageDate = computed(() => {
  const value = lastMessageInChat.value?.created_at || props.chat.timestamp;
  if (!value) return '';

  const numericValue = Number(value);
  const parsedValue = Number.isFinite(numericValue)
    ? numericValue < 1000000000000
      ? numericValue * 1000
      : numericValue
    : value;
  const date = new Date(parsedValue);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date);
});

const waitingSinceTimestamp = computed(() => {
  const value = props.chat.waiting_since;
  if (!value) return null;

  if (typeof value === 'number') {
    return value < 1000000000000 ? value * 1000 : value;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
});

const isWaitingOverOneDay = computed(() => {
  if (props.chat.status !== 'open' || !waitingSinceTimestamp.value) return false;

  return Date.now() - waitingSinceTimestamp.value >= 24 * 60 * 60 * 1000;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
</script>

<template>
  <div
    class="relative flex items-center gap-3 flex-grow-0 flex-shrink-0 w-auto max-w-full min-h-[68px] px-3 py-0 cursor-pointer conversation border-b border-n-slate-3 hover:border-n-surface-1 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group hover:z-[1] before:content-[none] before:absolute before:-top-px before:inset-x-0 before:h-px before:bg-n-surface-1 before:pointer-events-none hover:before:content-['']"
    :class="{
      'active animate-card-select bg-n-background !border-n-surface-1':
        isActiveChat,
      'selected bg-n-slate-2 !border-n-surface-1': selected,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="44"
        rounded-full
        hide-offline-status
      >
        <template #badge>
          <span
            class="absolute z-20 flex items-center justify-center rounded-full border-2 border-n-surface-1 size-5 ltr:-right-1 rtl:-left-1 -bottom-1 text-white"
            :class="sourceVisual.className"
          >
            <Icon :icon="sourceVisual.icon" class="size-3" />
          </span>
        </template>
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="py-2.5 flex-1 min-w-0 border-line">
      <div class="flex items-center gap-2 min-w-0 h-5">
        <h4
          class="conversation--user text-sm my-0 capitalize text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 text-n-slate-12"
          :class="hasUnread ? 'font-semibold' : 'font-medium'"
        >
          {{ currentContact.name }}
        </h4>
        <CardPriorityIcon
          :priority="chat.priority"
          class="flex-shrink-0 !size-3.5"
        />
        <span
          v-tooltip="isWaitingOverOneDay ? 'Ждёт ответа больше суток' : ''"
          class="flex-shrink-0 font-normal leading-4 text-xxs text-n-slate-10"
        >
          {{ lastMessageDate }}
        </span>
      </div>
      <div class="flex items-center gap-2 min-w-0 h-6">
        <VoiceCallStatus
          v-if="voiceCallData.status"
          key="voice-status-row"
          class="flex-1 min-w-0"
          :status="voiceCallData.status"
          :direction="voiceCallData.direction"
          :message-preview-class="messagePreviewClass"
        />
        <MessagePreview
          v-else-if="lastMessageInChat"
          key="message-preview"
          :message="lastMessageInChat"
          class="my-0 leading-6 h-6 flex-1 min-w-0 text-sm"
          :class="messagePreviewClass"
        />
        <p
          v-else
          key="no-messages"
          class="text-n-slate-11 text-sm my-0 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
          :class="messagePreviewClass"
        >
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </p>
        <UnreadBadge v-if="hasUnread" :count="unreadCount" />
      </div>
    </div>
  </div>
</template>
